import {
  ChevronDownIcon,
  PrinterIcon,
  UserIcon,
  CalendarIcon,
  TagIcon,
} from '@heroicons/react/outline';

import { useEffect, useState, useRef } from 'react';
import swal from 'sweetalert';
import { selectCurrentUser } from '../../Features/auth/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import LoadingBar from 'react-top-loading-bar';
import '../../Pagination.css';
import './ManageReceiptsView.css';

import { motion, AnimatePresence } from 'framer-motion';

import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';

import { format, addDays, subDays } from 'date-fns';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import { DateRangePicker } from 'react-date-range';

import { useGetUserEmployeesQuery } from '../../Features/userEmployeesSlice';

import {
  useGetSalesByDiscountNameByDateRangeMutation,
  useGetSalesByDiscountNameQuery,
} from '../../Features/userReportsSlice';

import {
  selectDateRange,
  setDateRange,
} from '../../Features/date/dateSlice';

import { useTheme } from '../../ThemeContext';


function ManageSalesByDiscountNameView({}) {

  const { darkMode } = useTheme();

  const user = useSelector(selectCurrentUser);
  const date_range = useSelector(selectDateRange);
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);

  const [currentItems, setCurrentItems] = useState([]);

  const [open, setOpen] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState('');

  const pickerRef = useRef(null);

  const [isExporting, setIsExporting] = useState(false);

  const [isSelecting, setIsSelecting] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(false);

  const [selectedEndDate, setSelectedEndDate] = useState(false);

  const [state, setState] = useState([
    {
      startDate: subDays(new Date(), 7),
      endDate: addDays(new Date(), 1),
      key: 'selection',
    },
  ]);


  /*
  |--------------------------------------------------------------------------
  | DATE RANGE
  |--------------------------------------------------------------------------
  */

  const initialStartDate = date_range
    ? format(date_range.startDate, 'yyyy-MM-dd')
    : format(subDays(new Date(), 7), 'yyyy-MM-dd');

  const initialEndDate = date_range
    ? format(date_range.endDate, 'yyyy-MM-dd')
    : format(new Date(), 'yyyy-MM-dd');


  /*
  |--------------------------------------------------------------------------
  | REPORT QUERY
  |--------------------------------------------------------------------------
  */

  const {
    data,
    isLoading,
    isSuccess,
  } = useGetSalesByDiscountNameQuery({
    user_id: user.id,
    start_date: initialStartDate,
    end_date: initialEndDate,
    employee_id: '0',
  });


  /*
  |--------------------------------------------------------------------------
  | EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const {
    data: employees,
  } = useGetUserEmployeesQuery({
    account_id: user.id,
    paginate: 10,
  });


  /*
  |--------------------------------------------------------------------------
  | INITIAL DATA / OUTSIDE CLICK
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    setProgress(40);

    if (isLoading) {
      setProgress(80);
    }

    if (isSuccess && data) {
      setCurrentItems(data.data || []);
    }

    setProgress(100);


    const handleClickOutside = (event) => {

      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }

    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };

  }, [data, isLoading, isSuccess]);


  /*
  |--------------------------------------------------------------------------
  | CUSTOM DATE RANGE QUERY
  |--------------------------------------------------------------------------
  */

  const [fetchCustomData] =
    useGetSalesByDiscountNameByDateRangeMutation();


  /*
  |--------------------------------------------------------------------------
  | DATE RANGE SELECTION
  |--------------------------------------------------------------------------
  */

  const handleSelect = async (ranges) => {

    const { selection } = ranges;

    setState([selection]);


    /*
     * The DateRangePicker fires once when the first
     * date is selected and again when the range is complete.
     */
    if (!isSelecting) {

      setIsSelecting(true);

      return;
    }


    setIsSelecting(false);
    setOpen(false);


    const startDate = format(
      selection.startDate,
      'yyyy-MM-dd'
    );

    const endDate = format(
      selection.endDate,
      'yyyy-MM-dd'
    );


    dispatch(
      setDateRange({
        date_range: selection,
      })
    );


    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);


    try {

      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        start_date: startDate,
        end_date: endDate,
        employee_id: selectedEmployee
          ? selectedEmployee
          : '0',
      }).unwrap();


      if (resp.success) {

        setCurrentItems(resp.data || []);

      } else {

        setCurrentItems([]);

        swal(
          'No results found.',
          'No sales records were found for the selected period.'
        );
      }

    } catch (err) {

      console.error(err);

      setCurrentItems([]);

      swal(
        'Error',
        'Data not found'
      );

    } finally {

      setProgress(100);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | EMPLOYEE FILTER
  |--------------------------------------------------------------------------
  */

  const handleEmployeeChange = async (
    e,
    employeeId
  ) => {

    e.preventDefault();

    const employee_id = employeeId || '0';

    setSelectedEmployee(
      employeeId || ''
    );


    try {

      setProgress(80);

      const resp = await fetchCustomData({
        user_id: user.id,
        start_date: selectedStartDate || initialStartDate,
        end_date: selectedEndDate || initialEndDate,
        employee_id,
      }).unwrap();


      if (resp.success) {

        setCurrentItems(
          resp.data || []
        );

      } else {

        setCurrentItems([]);

      }

    } catch (err) {

      console.error(err);

      setCurrentItems([]);

      swal(
        'No results found...',
        'No sales records were found for the selected employee.'
      );

    } finally {

      setProgress(100);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | CSV ESCAPE
  |--------------------------------------------------------------------------
  */

  const escapeCsvValue = (value) => {

    return `"${String(value ?? '').replace(
      /"/g,
      '""'
    )}"`;

  };


  /*
  |--------------------------------------------------------------------------
  | EXPORT CSV
  |--------------------------------------------------------------------------
  */

  const handleExportCSV = async () => {

    const employee_id =
      selectedEmployee || 0;

    const startDate =
      selectedStartDate ||
      initialStartDate;

    const endDate =
      selectedEndDate ||
      initialEndDate;


    try {

      setIsExporting(true);


      const response = await fetch(
        `http://127.0.0.1:8000/user/sales-by-discounts-name/${user.id}/${startDate}/${endDate}/${employee_id}`
      );


      const result =
        await response.json();


      if (
        !result.success ||
        !result.data?.length
      ) {

        swal(
          'No data',
          'No records found to export',
          'info'
        );

        return;
      }


      const report =
        result.data;


      const headers = [
        'Discount Name',
        'Times Applied',
        'Total Discount Amount',
      ];


      const rows = report.map(
        (item) => [

          item.is_total
            ? String(
                item.discount_name || ''
              ).toUpperCase()
            : item.discount_name,

          item.times_applied,

          item.total_discount_amount,

        ]
      );


      const csvContent = [
        headers,
        ...rows,
      ]
        .map((row) =>
          row
            .map(escapeCsvValue)
            .join(',')
        )
        .join('\n');


      const blob = new Blob(
        [csvContent],
        {
          type: 'text/csv;charset=utf-8;',
        }
      );


      const url =
        URL.createObjectURL(blob);


      const link =
        document.createElement('a');


      link.href = url;

      link.download =
        `sales_by_discount_name_${new Date()
          .toISOString()
          .split('T')[0]}.csv`;


      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      URL.revokeObjectURL(url);

    } catch (error) {

      console.error(
        'CSV Export Error:',
        error
      );

      swal(
        'Error',
        'Something went wrong while exporting data',
        'error'
      );

    } finally {

      setIsExporting(false);

    }

  };


  /*
  |--------------------------------------------------------------------------
  | MONEY FORMAT
  |--------------------------------------------------------------------------
  */

  const formatMoney = (value) => {

    return Number(
      value ?? 0
    ).toLocaleString(
      'en-ZM',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

  };


  /*
  |--------------------------------------------------------------------------
  | TOTAL DISCOUNT
  |--------------------------------------------------------------------------
  */

  const getTotalDiscount = () => {

    const totalRow =
      currentItems.find(
        (item) => item.is_total
      );


    if (totalRow) {

      return Number(
        totalRow.total_discount_amount || 0
      );

    }


    return currentItems.reduce(
      (total, item) =>
        total +
        Number(
          item.total_discount_amount || 0
        ),
      0
    );

  };


  /*
  |--------------------------------------------------------------------------
  | TOTAL APPLICATIONS
  |--------------------------------------------------------------------------
  */

  const getTotalApplications = () => {

    const totalRow =
      currentItems.find(
        (item) => item.is_total
      );


    if (totalRow) {

      return Number(
        totalRow.times_applied || 0
      );

    }


    return currentItems.reduce(
      (total, item) =>
        total +
        Number(
          item.times_applied || 0
        ),
      0
    );

  };


  /*
  |--------------------------------------------------------------------------
  | DATE DISPLAY
  |--------------------------------------------------------------------------
  */

  const displayedStartDate =
    date_range
      ? date_range.startDate
      : state[0].startDate;


  const displayedEndDate =
    date_range
      ? date_range.endDate
      : state[0].endDate;


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div className="
      w-full
      min-h-screen
      bg-gray-100
      dark:bg-gray-950
      pb-12
    ">

      <LoadingBar
        color="#80b643"
        height="4px"
        progress={progress}
        onLoaderFinished={() =>
          setProgress(0)
        }
      />


      <div className="
        w-full
        px-4
        sm:px-6
        lg:px-8
        py-6
      ">


        {/* =========================================================
            PAGE HEADER
        ========================================================= */}

        <div className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-4
          mb-6
        ">

          <div className="
            flex
            items-center
            gap-3
          ">

            <div className="
              w-11
              h-11
              rounded-xl
              bg-[#80b643]/10
              dark:bg-[#80b643]/20
              flex
              items-center
              justify-center
              flex-shrink-0
            ">

              <TagIcon
                className="
                  w-6
                  h-6
                  text-[#80b643]
                "
              />

            </div>


            <div>

              <h1 className="
                text-lg
                sm:text-xl
                font-semibold
                text-gray-800
                dark:text-white
              ">
                Sales by Discount
              </h1>


              <p className="
                text-sm
                text-gray-500
                dark:text-gray-400
              ">
                View discount usage and the total amount discounted
              </p>

            </div>

          </div>

        </div>


        {/* =========================================================
            FILTER CARD
        ========================================================= */}

        <div className="
          bg-white
          dark:bg-gray-900
          border
          border-gray-200
          dark:border-gray-800
          rounded-xl
          shadow-sm
          p-4
          mb-5
        ">

          <div className="
            flex
            flex-col
            lg:flex-row
            lg:items-end
            lg:justify-between
            gap-4
          ">


            {/* REPORT PERIOD */}

            <div className="
              flex
              flex-col
              gap-2
              w-full
              lg:w-auto
            ">

              <div>

                <p className="
                  text-xs
                  font-semibold
                  uppercase
                  tracking-wider
                  text-gray-500
                  dark:text-gray-400
                ">
                  Report Period
                </p>

                <p className="
                  text-xs
                  text-gray-400
                  dark:text-gray-500
                  mt-0.5
                ">
                  Select a date range
                </p>

              </div>


              <div
                className="
                  relative
                  inline-block
                  w-full
                  lg:w-auto
                "
                ref={pickerRef}
              >

                <button
                  type="button"
                  onClick={() =>
                    setOpen(!open)
                  }
                  className="
                    flex
                    items-center
                    justify-center
                    sm:justify-start
                    gap-2
                    w-full
                    lg:w-auto
                    min-w-0
                    sm:min-w-[260px]
                    bg-gray-50
                    dark:bg-gray-800
                    border
                    border-gray-200
                    dark:border-gray-700
                    hover:border-[#80b643]
                    dark:hover:border-[#80b643]
                    px-3
                    py-2.5
                    rounded-lg
                    text-sm
                    text-gray-700
                    dark:text-gray-200
                    transition-colors
                    focus:outline-none
                  "
                >

                  <CalendarIcon
                    className="
                      w-4
                      h-4
                      text-[#80b643]
                      flex-shrink-0
                    "
                  />

                  <span className="truncate">

                    {format(
                      displayedStartDate,
                      'MMM dd, yyyy'
                    )}

                    <span className="
                      mx-1.5
                      text-gray-400
                    ">
                      →
                    </span>

                    {format(
                      displayedEndDate,
                      'MMM dd, yyyy'
                    )}

                  </span>

                </button>


                <AnimatePresence>

                  {open && (

                    <motion.div
                      initial={{
                        opacity: 0,
                        y: -8,
                      }}
                      animate={{
                        opacity: 1,
                        y: 0,
                      }}
                      exit={{
                        opacity: 0,
                        y: -8,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: 'easeInOut',
                      }}
                      className="
                        absolute
                        z-50
                        mt-2
                        left-0
                        max-w-[calc(100vw-2rem)]
                        sm:max-w-none
                        overflow-x-auto
                        bg-white
                        dark:bg-gray-900
                        shadow-xl
                        border
                        border-gray-200
                        dark:border-gray-700
                        rounded-xl
                        p-3
                      "
                    >

                      <DateRangePicker
                        onChange={handleSelect}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        months={1}
                        ranges={state}
                        direction="horizontal"
                      />

                    </motion.div>

                  )}

                </AnimatePresence>

              </div>

            </div>


            {/* EMPLOYEE FILTER */}

            <div className="
              flex
              flex-col
              gap-2
              w-full
              lg:w-auto
            ">

              <div>

                <p className="
                  text-xs
                  font-semibold
                  uppercase
                  tracking-wider
                  text-gray-500
                  dark:text-gray-400
                ">
                  Employee
                </p>

                <p className="
                  text-xs
                  text-gray-400
                  dark:text-gray-500
                  mt-0.5
                ">
                  Filter sales by employee
                </p>

              </div>


              <div className="
                relative
                w-full
                lg:w-[260px]
              ">

                <UserIcon
                  className="
                    absolute
                    left-3
                    top-1/2
                    -translate-y-1/2
                    w-4
                    h-4
                    text-[#80b643]
                    pointer-events-none
                  "
                />


                <select
                  value={selectedEmployee}
                  onChange={(e) => {

                    handleEmployeeChange(
                      e,
                      e.target.value
                    );

                  }}
                  className="
                    appearance-none
                    w-full
                    bg-gray-50
                    dark:bg-gray-800
                    border
                    border-gray-200
                    dark:border-gray-700
                    pl-9
                    pr-9
                    py-2.5
                    rounded-lg
                    text-sm
                    text-gray-700
                    dark:text-gray-200
                    hover:border-[#80b643]
                    focus:outline-none
                    focus:border-[#80b643]
                    focus:ring-1
                    focus:ring-[#80b643]/30
                    cursor-pointer
                    transition-colors
                  "
                >

                  <option value="">
                    All Employees
                  </option>


                  {employees?.data?.data?.map(
                    (item) => (

                      <option
                        key={item.id}
                        value={item.id}
                      >
                        {item.fname} {item.lname}
                      </option>

                    )
                  )}

                </select>


                <ChevronDownIcon
                  className="
                    absolute
                    right-3
                    top-1/2
                    -translate-y-1/2
                    w-4
                    h-4
                    text-gray-400
                    pointer-events-none
                  "
                />

              </div>

            </div>

          </div>

        </div>


        {/* =========================================================
            SUMMARY CARDS
        ========================================================= */}

        {currentItems.length > 0 && (

          <div className="
            grid
            grid-cols-1
            sm:grid-cols-2
            gap-4
            mb-5
          ">

            {/* TOTAL DISCOUNTS */}

            <div className="
              bg-white
              dark:bg-gray-900
              border
              border-gray-200
              dark:border-gray-800
              rounded-xl
              shadow-sm
              p-4
            ">

              <div className="
                flex
                items-center
                justify-between
                gap-3
              ">

                <div>

                  <p className="
                    text-xs
                    font-medium
                    text-gray-500
                    dark:text-gray-400
                  ">
                    Discounts Applied
                  </p>

                  <p className="
                    text-xl
                    font-semibold
                    text-gray-800
                    dark:text-white
                    mt-1
                  ">
                    {getTotalApplications().toLocaleString()}
                  </p>

                </div>


                <div className="
                  w-10
                  h-10
                  rounded-lg
                  bg-[#80b643]/10
                  dark:bg-[#80b643]/20
                  flex
                  items-center
                  justify-center
                ">

                  <TagIcon
                    className="
                      w-5
                      h-5
                      text-[#80b643]
                    "
                  />

                </div>

              </div>

            </div>


            {/* TOTAL DISCOUNT AMOUNT */}

            <div className="
              bg-white
              dark:bg-gray-900
              border
              border-gray-200
              dark:border-gray-800
              rounded-xl
              shadow-sm
              p-4
            ">

              <div className="
                flex
                items-center
                justify-between
                gap-3
              ">

                <div>

                  <p className="
                    text-xs
                    font-medium
                    text-gray-500
                    dark:text-gray-400
                  ">
                    Total Discount Amount
                  </p>

                  <p className="
                    text-xl
                    font-semibold
                    text-gray-700
                    mt-1
                  ">
                    K {formatMoney(
                      getTotalDiscount()
                    )}
                  </p>

                </div>


                <div className="
                  w-10
                  h-10
                  rounded-lg
                  bg-[#80b643]/10
                  dark:bg-[#80b643]/20
                  flex
                  items-center
                  justify-center
                ">

                  <span className="
                    text-lg
                    font-semibold
                    text-[#80b643]
                  ">
                    K
                  </span>

                </div>

              </div>

            </div>

          </div>

        )}


        {/* =========================================================
            MAIN TABLE CARD
        ========================================================= */}

        <div className="
          bg-white
          dark:bg-gray-900
          border
          border-gray-200
          dark:border-gray-800
          rounded-xl
          shadow-sm
          overflow-hidden
        ">


          {/* TABLE HEADER */}

          <div className="
            flex
            flex-col
            sm:flex-row
            sm:items-center
            sm:justify-between
            gap-3
            px-4
            sm:px-5
            py-4
            border-b
            border-gray-200
            dark:border-gray-800
          ">

            <div>

              <h2 className="
                text-sm
                sm:text-base
                font-semibold
                text-gray-800
                dark:text-white
              ">
                Discount Sales Report
              </h2>

              <p className="
                text-xs
                sm:text-sm
                text-gray-500
                dark:text-gray-400
                mt-0.5
              ">
                Breakdown of discounts applied to ticket sales
              </p>

            </div>


            {/* EXPORT */}

            <button
              type="button"
              onClick={handleExportCSV}
              disabled={isExporting}
              className={`
                inline-flex
                items-center
                justify-center
                gap-2
                w-full
                sm:w-auto
                px-4
                py-2.5
                rounded-lg
                text-sm
                font-medium
                transition-all
                ${
                  isExporting
                    ? `
                      bg-gray-300
                      dark:bg-gray-700
                      text-gray-500
                      dark:text-gray-400
                      cursor-not-allowed
                    `
                    : `
                      bg-[#80b643]
                      hover:bg-[#719f3a]
                      text-white
                      shadow-sm
                      hover:shadow-md
                    `
                }
              `}
            >

              <PrinterIcon className="w-4 h-4" />

              {isExporting
                ? 'Exporting...'
                : 'Export CSV'}

            </button>

          </div>


          {/* =======================================================
              TABLE
          ======================================================= */}

          {currentItems.length > 0 ? (

            <div className="overflow-x-auto">

              <table className="
                w-full
                min-w-[700px]
              ">

                <thead className="
                  bg-gray-50
                  dark:bg-gray-800/70
                  border-b
                  border-gray-200
                  dark:border-gray-800
                ">

                  <tr>

                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-left
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Discount Name
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Discounts Applied
                    </th>


                    <th
                      scope="col"
                      className="
                        px-5
                        py-3.5
                        text-right
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-wider
                        text-gray-500
                        dark:text-gray-400
                      "
                    >
                      Amount Discounted
                    </th>

                  </tr>

                </thead>


                <tbody className="
                  divide-y
                  divide-gray-100
                  dark:divide-gray-800
                ">

                  {currentItems.map(
                    (data, index) => (

                      <tr
                        key={`${data.discount_name}-${index}`}
                        className={`
                          transition-colors
                          ${
                            data.is_total
                              ? `
                                bg-gray-50
                                dark:bg-gray-800/50
                                border-t-2
                                border-gray-200
                                dark:border-gray-700
                              `
                              : `
                                hover:bg-gray-50
                                dark:hover:bg-gray-800/50
                              `
                          }
                        `}
                      >


                        {/* DISCOUNT NAME */}

                        <td className="
                          px-5
                          py-4
                        ">

                          <div className="
                            flex
                            items-center
                            gap-3
                          ">

                            {!data.is_total && (

                              <div className="
                                w-9
                                h-9
                                rounded-lg
                                bg-[#80b643]/10
                                dark:bg-[#80b643]/20
                                flex
                                items-center
                                justify-center
                                flex-shrink-0
                              ">

                                <TagIcon
                                  className="
                                    w-4
                                    h-4
                                    text-[#80b643]
                                  "
                                />

                              </div>

                            )}


                            {data.is_total && (

                              <div className="
                                w-9
                                h-9
                                rounded-lg
                                bg-[#80b643]/10
                                dark:bg-[#80b643]/20
                                flex
                                items-center
                                justify-center
                                flex-shrink-0
                              ">

                                <span className="
                                  text-xs
                                  font-bold
                                  text-[#80b643]
                                ">
                                  Σ
                                </span>

                              </div>

                            )}


                            <div>

                              <div className={`
                                text-sm
                                ${
                                  data.is_total
                                    ? `
                                      font-semibold
                                      text-gray-800
                                      dark:text-white
                                    `
                                    : `
                                      font-medium
                                      text-gray-700
                                      dark:text-gray-200
                                    `
                                }
                              `}>

                                {data.is_total
                                  ? String(
                                      data.discount_name || ''
                                    ).toUpperCase()
                                  : data.discount_name}

                              </div>


                              {!data.is_total && (

                                <div className="
                                  text-xs
                                  text-gray-400
                                  dark:text-gray-500
                                  mt-0.5
                                ">
                                  Discount
                                </div>

                              )}

                            </div>

                          </div>

                        </td>


                        {/* TIMES APPLIED */}

                        <td className="
                          px-5
                          py-4
                          text-right
                        ">

                          <span className={`
                            inline-flex
                            items-center
                            justify-center
                            min-w-[40px]
                            px-2.5
                            py-1
                            rounded-lg
                            text-sm
                            ${
                              data.is_total
                                ? `
                                  bg-[#80b643]/10
                                  dark:bg-[#80b643]/20
                                  text-gray-700
                                  font-semibold
                                `
                                : `
                                  bg-gray-100
                                  dark:bg-gray-800
                                  text-gray-700
                                  dark:text-gray-300
                                  font-medium
                                `
                            }
                          `}>

                            {Number(
                              data.times_applied || 0
                            ).toLocaleString()}

                          </span>

                        </td>


                        {/* AMOUNT */}

                        <td className="
                          px-5
                          py-4
                          text-right
                        ">

                          <span className={`
                            text-sm
                            ${
                              data.is_total
                                ? `
                                  font-semibold
                                  text-gray-700
                                `
                                : `
                                  font-medium
                                  text-gray-700
                                  dark:text-gray-200
                                `
                            }
                          `}>

                            K {formatMoney(
                              data.total_discount_amount
                            )}

                          </span>

                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          ) : (

            /* =====================================================
               EMPTY STATE
            ===================================================== */

            <div className="
              flex
              flex-col
              items-center
              justify-center
              px-6
              py-16
            ">

              <div className="
                w-14
                h-14
                rounded-xl
                bg-gray-100
                dark:bg-gray-800
                flex
                items-center
                justify-center
                mb-4
              ">

                <TagIcon
                  className="
                    w-7
                    h-7
                    text-gray-400
                    dark:text-gray-500
                  "
                />

              </div>


              <h3 className="
                text-sm
                font-semibold
                text-gray-700
                dark:text-gray-200
              ">
                No discount sales found
              </h3>


              <p className="
                text-xs
                sm:text-sm
                text-gray-500
                dark:text-gray-400
                text-center
                max-w-sm
                mt-1.5
              ">
                No discount records match the selected date range or employee filter.
              </p>

            </div>

          )}

        </div>


        {/* =========================================================
            FOOTER INFORMATION
        ========================================================= */}

        {currentItems.length > 0 && (

          <div className="
            flex
            flex-col
            sm:flex-row
            sm:items-center
            sm:justify-between
            gap-2
            px-1
            mt-3
          ">

            <div className="
              text-xs
              text-gray-500
              dark:text-gray-400
            ">

              Showing{' '}

              <span className="
                font-medium
                text-gray-700
                dark:text-gray-200
              ">
                {currentItems.length}
              </span>{' '}

              discount
              {currentItems.length === 1
                ? ''
                : 's'}

            </div>


            <div className="
              text-xs
              text-gray-400
              dark:text-gray-500
            ">

              {format(
                displayedStartDate,
                'MMM dd, yyyy'
              )}

              {' → '}

              {format(
                displayedEndDate,
                'MMM dd, yyyy'
              )}

            </div>

          </div>

        )}

      </div>

    </div>

  );
}


export default ManageSalesByDiscountNameView;